import { useMutation } from "@tanstack/react-query";
import api from "apis";
import { GetUploadPresignedUrlParams } from "apis/data-contracts";
import axios from "axios";

type UsePresignedUrlMutationProps = Partial<GetUploadPresignedUrlParams>;

const usePresignedUrlMutation = ({
  ...defaultOptions
}: UsePresignedUrlMutationProps = {}) => {
  const getPresignedUrl = useMutation({
    mutationFn: api.s3.getUploadPresignedUrl,
  });

  const putPresignedUrl = useMutation({
    mutationFn: ({
      presignedUrl,
      file,
    }: {
      presignedUrl: string;
      file: File;
    }) => {
      return axios({
        method: "PUT",
        url: presignedUrl,
        data: file,
        headers: {
          "Content-Type": file.type,
        },
      });
    },
  });

  const mutate = async (
    file: File,
    overrideOptions?: Partial<GetUploadPresignedUrlParams>,
  ) => {
    const options = {
      ...defaultOptions,
      ...overrideOptions,
    };

    if (!options.fileName) {
      throw new Error("fileName is required to generate presigned URL");
    }

    if (!options.fileType) {
      throw new Error("fileType is required to generate presigned URL");
    }

    if (!options.allowedExtensions) {
      throw new Error(
        "allowedExtensions is required to generate presigned URL",
      );
    }

    if (!options.isOverwrite) {
      throw new Error("isOverwrite is required to generate presigned URL");
    }

    return mutateWithFileName(file, options as GetUploadPresignedUrlParams);
  };

  const mutateWithFileName = async (
    file: File,
    overrideOptions: GetUploadPresignedUrlParams,
  ) => {
    const options = {
      ...defaultOptions,
      ...overrideOptions,
    };

    const data = await getPresignedUrl.mutateAsync({
      ...options,
      fileName: `${options.fileName}`,
    });
    const presignedUrl = data.data.result.uploadInfo.presignedUrl;

    await putPresignedUrl.mutateAsync({
      presignedUrl,
      file,
    });
    const s3Url = new URL(presignedUrl); // URL 객체 생성
    s3Url.search = ""; // 모든 쿼리 파라미터 제거

    return s3Url.toString();
  };

  return {
    mutate,
    mutateWithFileName,
    error: getPresignedUrl.error || putPresignedUrl.error,
  };
};

export default usePresignedUrlMutation;
